import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LinkIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { ColorInput } from 'components/ColorPickers'
import { useAuth } from 'context/user'
import { useEntitlements, planDisplayName } from 'context/plan'
import { AccountOverlayLinkState, useAccountOverlayLink } from 'hooks/useAccountOverlayLink'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { graphqlRequest } from 'lib/helpers'
import { sortByName } from 'lib/elementOrder'
import { useYouTubeConnection } from 'hooks/useYouTubeConnection'
import { useTwitchConnection } from 'hooks/useTwitchConnection'
import {
  SESSION_REFUSAL_COPY,
  useYouTubeConnectionIdentity,
  YOUTUBE_SESSION_ENDED,
  YouTubeSessionWidget,
  YouTubeTermsFooter
} from 'components/YouTubeSessionWidget'
import { useAccountYouTubeSessions, YouTubeSessionsStore } from 'hooks/useYouTubeSessions'
import {
  ProviderBrandIcon,
  TWITCH_BRAND_ICON_SRC,
  YOUTUBE_BRAND_ICON_SRC
} from 'components/ProviderBrandIcon'
import { ChatOverlayMockup } from 'components/overlayPreview/OverlayMockups'
import { ChatOverlayPreviewConfig } from 'components/overlayPreview/previewConfig'
import { usePrefersReducedMotion } from 'components/overlayPreview/previewFeeds'
import {
  ChatOverlaySummary,
  DeletionResult,
  Plan,
  YouTubeChatSession,
  YouTubeChatSessionResult,
  YouTubeChatSourceResult
} from 'types/api'
import createChatOverlayMutation from 'graphql/createChatOverlay.graphql'
import updateChatOverlayMutation from 'graphql/updateChatOverlay.graphql'
import updateChatOverlayYouTubeChannelsMutation from 'graphql/updateChatOverlayYouTubeChannels.graphql'
import resolveYouTubeChatSourceMutation from 'graphql/resolveYouTubeChatSource.graphql'
import listMyChatOverlaysQuery from 'graphql/listMyChatOverlays.graphql'
import startYouTubeChatSessionMutation from 'graphql/startYouTubeChatSession.graphql'
import stopYouTubeChatSessionMutation from 'graphql/stopYouTubeChatSession.graphql'
import {
  CHAT_FONT_OPTION_GROUPS,
  CHAT_MESSAGE_WEIGHT_DEFAULT,
  CHAT_USERNAME_WEIGHT_DEFAULT,
  ChatFontKey,
  DEFAULT_CHAT_FONT_OPTION,
  chatWeightChoices,
  effectiveChatFontWeight,
  normalizeChatFontKey
} from 'lib/chatFonts'
import { ensureFontLoaded } from 'lib/fontLoader'
import deleteChatOverlayMutation from 'graphql/deleteChatOverlay.graphql'
import duplicateChatOverlayMutation from 'graphql/duplicateChatOverlay.graphql'

const FALLBACK_MAX_CHANNELS = 3
const MIN_FONT_SIZE = 8
const MAX_FONT_SIZE = 72
const MIN_LINE_HEIGHT = 8
const MAX_LINE_HEIGHT = 120
const MIN_IMAGE_SCALE = 50
const MAX_IMAGE_SCALE = 300
const DEFAULT_MESSAGE_COLOR = '#EFEFF1'
const MAX_CHAT_DELAY_MS = 60_000

const clampChatDelayMs = (ms: number): number =>
  Math.min(MAX_CHAT_DELAY_MS, Math.max(0, Math.round(ms)))

const chatDelayMsOf = (chat: Pick<ChatOverlaySummary, 'chatDelayMs'>): number => {
  const raw = chat.chatDelayMs
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0
  return clampChatDelayMs(raw)
}

const MIN_CHAT_DIMENSION = 100
const MAX_CHAT_DIMENSION = 4000

const clampChatDimension = (n: number): number =>
  Math.min(MAX_CHAT_DIMENSION, Math.max(MIN_CHAT_DIMENSION, Math.round(n)))

const chatDimensionsOf = (
  chat: Pick<ChatOverlaySummary, 'width' | 'height'>
): { width: number | null, height: number | null } => {
  const w = chat.width
  const h = chat.height
  if (
    typeof w !== 'number' || !Number.isFinite(w) ||
    typeof h !== 'number' || !Number.isFinite(h)
  ) {
    return { width: null, height: null }
  }
  return { width: clampChatDimension(w), height: clampChatDimension(h) }
}

interface ChatDimensionPreset {
  key: string
  label: string
  width: number
  height: number
}

const CHAT_DIMENSION_PRESETS: ChatDimensionPreset[] = [
  { key: 'FULL', label: 'Full 1920x1080', width: 1920, height: 1080 },
  { key: 'LOWER_THIRD', label: 'Lower third 1080x300', width: 1080, height: 300 },
  { key: 'COLUMN', label: 'Column 480x1080', width: 480, height: 1080 },
  { key: 'CORNER', label: 'Corner 300x300', width: 300, height: 300 }
]

const chatDimensionPresetKeyOf = (width: number, height: number): string | null => {
  const match = CHAT_DIMENSION_PRESETS.find((p) => p.width === width && p.height === height)
  return match === undefined ? null : match.key
}

const obsSourceRecipeFor = (width: number, height: number): string =>
  `OBS source size: ${width * 2} x ${height * 2}, then scale it to 50% (Scale Filtering: Area).`

const chatFontWeightOf = (fontKey: string, raw: unknown, roleDefault: number): number =>
  effectiveChatFontWeight(fontKey, raw, roleDefault)

export type TextShadowStyle = 'OFF' | 'SOFT' | 'HARD'

const textShadowOf = (
  chat: Pick<ChatOverlaySummary, 'textShadow' | 'showTextShadow'>
): TextShadowStyle => {
  const raw = chat.textShadow
  if (raw === 'OFF' || raw === 'SOFT' || raw === 'HARD') return raw
  const legacy: unknown = chat.showTextShadow
  return legacy === false ? 'OFF' : 'SOFT'
}

const formatChatDelaySeconds = (ms: number): string => (ms / 1000).toFixed(3)

const parseChatDelaySeconds = (text: string): number | 'invalid' => {
  const trimmed = text.trim()
  if (trimmed === '') return 0
  const seconds = Number(trimmed)
  if (!Number.isFinite(seconds) || seconds < 0 || Object.is(seconds, -0)) return 'invalid'
  return clampChatDelayMs(seconds * 1000)
}

export const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

export const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

export interface ChatDraft {
  name: string
  twitchChatChannels: string[]
  youtubeSourceEnabled: boolean
  youtubeChatChannels: string[]
  textShadow: TextShadowStyle
  showBadges: boolean
  showChannelPills: boolean
  usernameFontSize: number
  usernameLineHeight: number
  messageFontSize: number
  messageLineHeight: number
  messageFontWeight: number
  usernameFontWeight: number
  imageScale: number
  messageColor: string
  blockedWordsText: string
  fontFamily: ChatFontKey
  chatDelayMs: number
  width: number | null
  height: number | null
  show7tvEmotes: boolean
  showBttvEmotes: boolean
  showFfzEmotes: boolean
  showYouTubeProfilePictures: boolean
}

type NumericChatField = {
  [K in keyof ChatDraft]: ChatDraft[K] extends number ? K : never
}[keyof ChatDraft]

const emptyDraft = (): ChatDraft => ({
  name: '',
  twitchChatChannels: [],
  youtubeSourceEnabled: false,
  youtubeChatChannels: [],
  textShadow: 'SOFT',
  showBadges: true,
  showChannelPills: true,
  usernameFontSize: 14,
  usernameLineHeight: 22,
  messageFontSize: 14,
  messageLineHeight: 22,
  messageFontWeight: CHAT_MESSAGE_WEIGHT_DEFAULT,
  usernameFontWeight: CHAT_USERNAME_WEIGHT_DEFAULT,
  imageScale: 100,
  messageColor: DEFAULT_MESSAGE_COLOR,
  blockedWordsText: '',
  fontFamily: 'INTER',
  chatDelayMs: 0,
  width: null,
  height: null,
  show7tvEmotes: true,
  showBttvEmotes: true,
  showFfzEmotes: true,
  showYouTubeProfilePictures: true
})

const youtubeChatChannelsOf = (
  chat: Pick<ChatOverlaySummary, 'youtubeChatChannels'>
): string[] => {
  const raw = chat.youtubeChatChannels
  if (!Array.isArray(raw)) return []
  return raw.filter((c): c is string => typeof c === 'string' && c !== '')
}

const draftFromChat = (chat: ChatOverlaySummary): ChatDraft => {
  const fontFamily = normalizeChatFontKey(chat.fontFamily)
  return {
    name: chat.name,
    twitchChatChannels: [...chat.twitchChatChannels],
    youtubeSourceEnabled: chat.youtubeSourceEnabled,
    youtubeChatChannels: youtubeChatChannelsOf(chat),
    textShadow: textShadowOf(chat),
    showBadges: chat.showBadges,
    showChannelPills: chat.showChannelPills,
    usernameFontSize: chat.usernameFontSize,
    usernameLineHeight: chat.usernameLineHeight,
    messageFontSize: chat.messageFontSize,
    messageLineHeight: chat.messageLineHeight,
    messageFontWeight: chatFontWeightOf(fontFamily, chat.messageFontWeight, CHAT_MESSAGE_WEIGHT_DEFAULT),
    usernameFontWeight: chatFontWeightOf(fontFamily, chat.usernameFontWeight, CHAT_USERNAME_WEIGHT_DEFAULT),
    imageScale: chat.imageScale,
    messageColor: chat.messageColor,
    blockedWordsText: (chat.blockedWords ?? []).join('\n'),
    fontFamily,
    chatDelayMs: chatDelayMsOf(chat),
    ...chatDimensionsOf(chat),
    show7tvEmotes: chat.show7tvEmotes !== false,
    showBttvEmotes: chat.showBttvEmotes !== false,
    showFfzEmotes: chat.showFfzEmotes !== false,
    showYouTubeProfilePictures: chat.showYouTubeProfilePictures !== false
  }
}

const normalizeLogin = (raw: string): string =>
  raw.trim().toLowerCase().replace(/^#/, '')

export const ownUnlistedLogin = (
  myTwitchLogin: string | null,
  channels: string[]
): string | null => {
  if (myTwitchLogin === null) return null
  const login = myTwitchLogin.toLowerCase()
  if (channels.some((c) => c.toLowerCase() === login)) return null
  return login
}

const parseBlockedWords = (raw: string): string[] =>
  [...new Set(raw.split('\n').map((w) => w.trim()).filter((w) => w !== ''))]

const chatPreviewConfigFromDraft = (draft: ChatDraft): ChatOverlayPreviewConfig => ({
  textShadow: draft.textShadow,
  showTextShadow: draft.textShadow !== 'OFF',
  showBadges: draft.showBadges,
  showChannelPills: draft.showChannelPills,
  usernameFontSize: draft.usernameFontSize,
  usernameLineHeight: draft.usernameLineHeight,
  messageFontSize: draft.messageFontSize,
  messageLineHeight: draft.messageLineHeight,
  messageFontWeight: draft.messageFontWeight,
  usernameFontWeight: draft.usernameFontWeight,
  imageScale: draft.imageScale,
  messageColor: draft.messageColor,
  blockedWords: parseBlockedWords(draft.blockedWordsText),
  fontFamily: draft.fontFamily,
  youtubeSourceEnabled: draft.youtubeSourceEnabled,
  youtubeChatChannels: draft.youtubeChatChannels,
  twitchChatChannels: draft.twitchChatChannels,
  show7tvEmotes: draft.show7tvEmotes,
  showBttvEmotes: draft.showBttvEmotes,
  showFfzEmotes: draft.showFfzEmotes,
  showYouTubeProfilePictures: draft.showYouTubeProfilePictures
})

const MOCKUP_STAGE_HEIGHT_PX = 280

const PREVIEW_CHECKERBOARD_BG: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, rgba(255,255,255,0.06) 25%, transparent 25%), ' +
    'linear-gradient(-45deg, rgba(255,255,255,0.06) 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.06) 75%), ' +
    'linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.06) 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0'
}

const MAX_DIMENSION_PREVIEW_SCALE = 2

const DimensionedPreviewBox = ({
  width,
  height,
  children
}: {
  width: number
  height: number
  children: React.ReactNode
}): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [measuredWidth, setMeasuredWidth] = useState(0)
  useEffect(() => {
    const el = containerRef.current
    if (el == null) return
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect
        if (rect != null) setMeasuredWidth(rect.width)
      })
      ro.observe(el)
      setMeasuredWidth(el.getBoundingClientRect().width)
      return () => ro.disconnect()
    }
    setMeasuredWidth(el.getBoundingClientRect().width)
  }, [])
  const scale = measuredWidth > 0
    ? Math.min(measuredWidth / width, MAX_DIMENSION_PREVIEW_SCALE)
    : 0
  return (
    <div
      ref={containerRef}
      data-testid='dimensioned-preview-box'
      className='overflow-hidden rounded-lg border border-white/10'
      style={{ ...PREVIEW_CHECKERBOARD_BG, height: Math.round(height * scale) }}
    >
      {scale > 0 && (
        <div
          style={{
            width,
            height,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export const OVERLAY_LINK_NOTE = "Paste this link, don't retype it. Anyone with it can show this overlay."

export const REGENERATE_LINK_CONFIRM =
  'This turns off every copy of this link. Re-paste the new link anywhere you use it.'

export const CopyButton = ({
  value,
  label,
  variant = 'icon',
  disabled = false,
  className = ''
}: {
  value: string
  label: string
  variant?: 'icon' | 'primary'
  disabled?: boolean
  className?: string
}): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      globalThis.setTimeout(() => setCopied(false), 1500)
    } catch {

    }
  }
  if (variant === 'primary') {
    return (
      <button
        type='button'
        onClick={() => { void handleCopy() }}
        disabled={disabled}
        title={`Copy ${label}`}
        className={`inline-flex items-center justify-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
      >
        {copied
          ? <ClipboardDocumentCheckIcon className='size-3.5 text-emerald-400' aria-hidden='true' />
          : <ClipboardDocumentIcon className='size-3.5' aria-hidden='true' />}
        <span aria-hidden='true'>{copied ? 'Copied' : 'Copy link'}</span>
        <span className='sr-only'>Copy {label}</span>
      </button>
    )
  }
  return (
    <button
      type='button'
      onClick={() => { void handleCopy() }}
      disabled={disabled}
      title={`Copy ${label}`}
      className={`rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    >
      {copied
        ? <ClipboardDocumentCheckIcon className='size-4 text-emerald-400' aria-hidden='true' />
        : <ClipboardDocumentIcon className='size-4' aria-hidden='true' />}
      <span className='sr-only'>Copy {label}</span>
    </button>
  )
}

const RegenerateLinkButton = ({
  link,
  label,
  variant
}: {
  link: AccountOverlayLinkState
  label: string
  variant: 'text' | 'icon'
}): JSX.Element => {
  const onClick = (): void => {
    if (!globalThis.confirm(REGENERATE_LINK_CONFIRM)) return
    void link.regenerate()
  }
  const icon = link.busy
    ? <LoadingSpinner />
    : <ArrowPathIcon className={variant === 'text' ? 'size-3.5' : 'size-4'} aria-hidden='true' />
  if (variant === 'text') {
    return (
      <button
        type='button'
        onClick={onClick}
        disabled={link.busy}
        title={`Make a new ${label} link and turn this one off`}
        className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
      >
        {icon}
        Regenerate
        <span className='sr-only'>{label} link</span>
      </button>
    )
  }
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={link.busy}
      title={`Regenerate the ${label} link (turns this one off)`}
      className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
    >
      {icon}
      <span className='sr-only'>Regenerate the {label} link</span>
    </button>
  )
}

export const OverlayLinkBlock = ({
  link,
  label = 'chat overlay'
}: {
  link: AccountOverlayLinkState
  label?: string
}): JSX.Element => {
  const { url } = link
  if (url === null) {
    if (link.error !== null && !link.busy) {
      return (
        <div className='flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
          <span className='text-[11px] text-red-300'>{link.error}</span>
          <button
            type='button'
            onClick={() => link.retry()}
            className='shrink-0 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            Retry
          </button>
        </div>
      )
    }
    return <div className='h-9 animate-pulse rounded-md bg-white/5' aria-hidden='true' />
  }
  return (
    <div className='space-y-2' data-testid='overlay-link-block'>
      <div className='flex min-w-0 items-center gap-2 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
        <LinkIcon className='size-4 shrink-0 text-dm-text-accent' aria-hidden='true' />
        <span className='min-w-0 select-all truncate text-[11px] text-brand'>{url}</span>
      </div>
      <div className='flex items-center gap-1.5'>
        <CopyButton
          value={url}
          label={`${label} link`}
          variant='primary'
          disabled={link.busy}
          className='flex-1'
        />
        <a
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          title={`Open ${label}`}
          className='rounded-md border border-white/10 p-1.5 text-dm-text-accent hover:border-white/20 hover:text-dm-text-primary'
        >
          <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
          <span className='sr-only'>Open {label}</span>
        </a>
        <RegenerateLinkButton link={link} label={label} variant='text' />
      </div>
      <p className='text-[11px] text-dm-text-accent'>{OVERLAY_LINK_NOTE}</p>
      {link.error !== null && <ErrorInline>{link.error}</ErrorInline>}
    </div>
  )
}

export const OverlayLinkRowActions = ({
  link,
  label
}: {
  link: AccountOverlayLinkState
  label: string
}): JSX.Element => {
  const { url } = link
  if (url === null) {
    if (link.error !== null && !link.busy) {
      return (
        <button
          type='button'
          onClick={() => link.retry()}
          title={link.error}
          className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10'
        >
          <ExclamationTriangleIcon className='size-3.5' aria-hidden='true' />
          Retry link
        </button>
      )
    }
    return <span className='inline-block h-6 w-24 animate-pulse rounded-md bg-white/5' aria-hidden='true' />
  }
  return (
    <>
      {link.error !== null && (
        <span title={link.error} className='p-1 text-red-300'>
          <ExclamationTriangleIcon className='size-4' aria-hidden='true' />
          <span className='sr-only'>{link.error}</span>
        </span>
      )}
      <CopyButton value={url} label={`${label} link`} disabled={link.busy} />
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        title={`Open ${label}`}
        className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
      >
        <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
        <span className='sr-only'>Open {label}</span>
      </a>
      <RegenerateLinkButton link={link} label={label} variant='icon' />
    </>
  )
}

const YOUTUBE_DISPLAY_ONLY = 'Display only'
const YOUTUBE_LIVE_PAUSED = 'Starting new YouTube live chats is paused right now.'

const youtubeListSentence = (cap: number | null, n: number): string =>
  cap === null
    ? `Unlimited YouTube channels per overlay (${n} added).`
    : `Up to ${cap} YouTube channels per overlay (${n}/${cap}).`

const youtubeCapLine = (
  cap: number | null,
  liveCap: number | null,
  n: number,
  running: number
): string => {
  if (liveCap === null || (cap !== null && liveCap === cap)) {
    return `${youtubeListSentence(cap, n)} ${YOUTUBE_DISPLAY_ONLY}`
  }
  const live = liveCap === 0
    ? YOUTUBE_LIVE_PAUSED
    : `Your account can run up to ${liveCap} YouTube live ${liveCap === 1 ? 'chat' : 'chats'} at a time (${running}/${liveCap} running).`
  return `${youtubeListSentence(cap, n)} ${live} ${YOUTUBE_DISPLAY_ONLY}`
}

const youtubeLiveCapNote = (liveCap: number): string => {
  if (liveCap === 0) return YOUTUBE_LIVE_PAUSED
  const subject = liveCap === 1 ? 'One YouTube live chat' : `${liveCap} YouTube live chats`
  return `${subject} can run at a time on your account. Stop one to start another.`
}

const youtubeUpgradeHint = (upgrade: Plan, liveCap: number | null): string => {
  const rungCap = upgrade.limits.youtubeChannelsPerOverlay ?? upgrade.limits.twitchChannelsPerOverlay
  const allows = rungCap === null ? 'unlimited' : `up to ${rungCap}`
  const live = liveCap !== null ? ` The live limit (${liveCap} at a time) is the same on every plan.` : ''
  return `${planDisplayName(upgrade)} allows ${allows} YouTube channels per overlay.${live}`
}

const hideBrokenPicture = (e: React.SyntheticEvent<HTMLImageElement>): void => {
  e.currentTarget.style.display = 'none'
}

const YOUTUBE_LINK_HELP = 'Paste the stream\'s Share link (youtube.com/live/...) or its watch?v= link. A channel page link will not work.'

const YouTubeChannelsBlock = ({
  connected,
  channels,
  savedChannels,
  chatId,
  onDraftAdd,
  onRemove,
  onSaved,
  cap,
  liveCap,
  upgrade,
  ownChannelId,
  ownChannelTitle,
  auth0Token,
  store,
  formBusy,
  onAddBusyChange
}: {
  connected: boolean
  channels: string[]
  savedChannels: string[] | null
  chatId: string | null
  onDraftAdd: (channelId: string) => void
  onRemove: (channelId: string) => void
  onSaved: (chat: ChatOverlaySummary) => void
  cap: number | null
  liveCap: number | null
  upgrade: Plan | null
  ownChannelId: string | null
  ownChannelTitle: string | null
  auth0Token: string | undefined
  store: YouTubeSessionsStore
  formBusy: boolean
  onAddBusyChange: (busy: boolean) => void
}): JSX.Element => {
  const [addUrl, setAddUrl] = useState('')
  const [lastLinks, setLastLinks] = useState<Record<string, string>>({})
  const [rowPrompt, setRowPrompt] = useState<{ channelId: string, url: string } | null>(null)
  const [busy, setBusy] = useState<{ kind: 'add' } | { kind: 'start' | 'stop', channelId: string } | null>(null)
  const [refusal, setRefusal] = useState<{ code: string, message: string | null, fallback: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [addSaved, setAddSaved] = useState(false)
  const addBusy = busy !== null && busy.kind === 'add'
  useEffect(() => {
    onAddBusyChange(addBusy)
  }, [addBusy, onAddBusyChange])
  useEffect(() => () => { onAddBusyChange(false) }, [onAddBusyChange])

  const atCap = cap !== null && channels.length >= cap
  const running = store.runningCount
  const atLiveCap = liveCap !== null && running >= liveCap

  const sessionFor = (channelId: string): YouTubeChatSession | null => {
    const exact = store.sessions.find((s) => s.channelId === channelId)
    if (exact !== undefined) return exact
    if (channelId === ownChannelId) {
      return store.sessions.find((s) => s.channelId == null) ?? null
    }
    return null
  }

  const titleFor = (channelId: string): string => {
    const cached = store.meta[channelId]?.title ?? null
    if (cached !== null) return cached
    if (channelId === ownChannelId && ownChannelTitle !== null) return ownChannelTitle
    return channelId
  }

  const saveAdded = async (channelId: string): Promise<void> => {
    if (chatId === null || auth0Token === undefined) {
      onDraftAdd(channelId)
      return
    }
    const saved = savedChannels ?? []
    if (!saved.includes(channelId)) {
      const youtubeChatChannels = [...new Set([...saved, channelId])]
      const { chat } = await graphqlRequest<{ chat: ChatOverlaySummary }>(
        updateChatOverlayYouTubeChannelsMutation, { chatId, youtubeChatChannels }, auth0Token
      )
      onSaved(chat)
    }
    onDraftAdd(channelId)
    setAddSaved(true)
  }

  const handleAdd = async (): Promise<void> => {
    const videoUrl = addUrl.trim()
    if (auth0Token === undefined || busy !== null || formBusy || videoUrl === '' || atCap) return
    setBusy({ kind: 'add' })
    setRefusal(null)
    setError(null)
    setAddSaved(false)
    try {
      const { result } = await graphqlRequest<{ result: YouTubeChatSourceResult | null }>(
        resolveYouTubeChatSourceMutation, { videoUrl }, auth0Token
      )
      const channelId = result?.channelId ?? null
      if (result == null || !result.resolved || channelId == null || channelId === '') {
        setRefusal({
          code: result?.refusalCode ?? '',
          message: result?.message ?? null,
          fallback: 'That link could not be added. Try again in a moment.'
        })
        return
      }
      store.rememberMeta(channelId, { title: result.channelTitle, imageUrl: result.channelProfileImageUrl })
      setLastLinks((prev) => ({ ...prev, [channelId]: videoUrl }))
      await saveAdded(channelId)
      setAddUrl('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the YouTube channel')
    } finally {
      setBusy(null)
    }
  }

  const handleQuickAdd = async (channelId: string): Promise<void> => {
    if (busy !== null || formBusy || atCap) return
    setBusy({ kind: 'add' })
    setRefusal(null)
    setError(null)
    setAddSaved(false)
    try {
      await saveAdded(channelId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the YouTube channel')
    } finally {
      setBusy(null)
    }
  }

  const openRowPrompt = (channelId: string): void => {
    const link = lastLinks[channelId]
    const last = sessionFor(channelId)?.videoId
    let url = ''
    if (link !== undefined) url = link
    else if (typeof last === 'string' && last !== '') url = `https://www.youtube.com/watch?v=${last}`
    setRowPrompt({ channelId, url })
    setRefusal(null)
    setError(null)
  }

  const handleRowStart = async (channelId: string): Promise<void> => {
    const videoUrl = rowPrompt !== null && rowPrompt.channelId === channelId
      ? rowPrompt.url.trim()
      : ''
    if (auth0Token === undefined || busy !== null || videoUrl === '' || atLiveCap) return
    setBusy({ kind: 'start', channelId })
    setRefusal(null)
    setError(null)
    try {
      const { result } = await graphqlRequest<{ result: YouTubeChatSessionResult }>(
        startYouTubeChatSessionMutation, { videoUrl }, auth0Token
      )
      if (result.session != null) store.upsert(result.session)
      if (!result.started && result.refusalCode !== null) {
        setRefusal({
          code: result.refusalCode,
          message: result.message,
          fallback: 'YouTube chat could not start. Try again in a moment.'
        })
      }
      const resolved = result.session?.channelId
      if (result.started && typeof resolved === 'string' && resolved !== channelId) {
        setError('That video belongs to a different channel. A row Start needs a live link from that row\'s own channel.')
        return
      }
      if (result.started) {
        setLastLinks((prev) => ({ ...prev, [channelId]: videoUrl }))
        setRowPrompt(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start YouTube chat')
    } finally {
      setBusy(null)
    }
  }

  const handleRowStop = async (channelId: string): Promise<void> => {
    if (auth0Token === undefined || busy !== null) return
    setBusy({ kind: 'stop', channelId })
    setRefusal(null)
    setError(null)
    try {
      const row = sessionFor(channelId)
      const variables = typeof row?.channelId === 'string' && row.channelId !== ''
        ? { channelId: row.channelId }
        : {}
      const resp = await graphqlRequest<{ session: YouTubeChatSession | null }>(
        stopYouTubeChatSessionMutation, variables, auth0Token
      )
      if (resp.session != null) store.upsert(resp.session)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop YouTube chat')
    } finally {
      setBusy(null)
    }
  }

  const blockLabel = (
    <FieldLabel>
      <span className='flex items-center gap-1.5'>
        <ProviderBrandIcon src={YOUTUBE_BRAND_ICON_SRC} size='sm' />
        YouTube channels
      </span>
    </FieldLabel>
  )

  if (!connected) {
    return (
      <div data-testid='youtube-channels-block'>
        {blockLabel}
        <p className='rounded-md border border-white/5 bg-dm-bg-accent/20 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
          Connect your YouTube channel on the{' '}
          <Link to='/connections' className='font-medium text-brand underline hover:text-brand/80'>
            Connections page
          </Link>{' '}
          to add YouTube live chats to this overlay.
        </p>
      </div>
    )
  }

  const quickAddChannelId = ownChannelId !== null && !atCap && !channels.includes(ownChannelId)
    ? ownChannelId
    : null

  return (
    <div data-testid='youtube-channels-block'>
      {blockLabel}

      {quickAddChannelId !== null && (
        <button
          type='button'
          onClick={() => { void handleQuickAdd(quickAddChannelId) }}
          disabled={busy !== null || formBusy}
          data-testid='own-youtube-channel-quick-add'
          className='mb-2 inline-flex items-center gap-1.5 rounded-md border border-brand/20 bg-brand/5 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-60'
        >
          <PlusIcon className='size-3.5 shrink-0 text-brand' aria-hidden='true' />
          Add your channel:{' '}
          <span className='font-semibold text-dm-text-primary'>
            {ownChannelTitle ?? quickAddChannelId}
          </span>
        </button>
      )}

      <div className='flex gap-1.5'>
        <input
          type='text'
          value={addUrl}
          onChange={(e) => setAddUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            void handleAdd()
          }}
          disabled={atCap || busy !== null || formBusy}
          placeholder='https://youtube.com/live/...'
          autoComplete='off'
          data-testid='youtube-add-url-input'
          className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'
        />
        <button
          type='button'
          onClick={() => { void handleAdd() }}
          disabled={atCap || busy !== null || formBusy || addUrl.trim() === ''}
          data-testid='youtube-add-button'
          className='inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          {busy !== null && busy.kind === 'add' && <LoadingSpinner />}
          Add
        </button>
      </div>
      <p className='mt-1 text-[11px] font-medium text-dm-text-secondary' data-testid='youtube-link-help'>
        {YOUTUBE_LINK_HELP}
      </p>
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        Add saves the channel to this overlay. Press Start on its row while
        the stream is live.
      </p>
      {chatId === null && (
        <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='youtube-add-create-note'>
          Saved with the overlay when you press Create chat overlay.
        </p>
      )}
      {chatId !== null && addSaved && (
        <p className='mt-1 text-[11px] text-emerald-300' data-testid='youtube-add-saved-note'>
          Added and saved to this overlay.
        </p>
      )}
      <ul className='mt-2 space-y-1.5'>
        {channels.map((channelId) => {
          const row = sessionFor(channelId)
          const rowRunning = row !== null && row.status === 'RUNNING'
          const startBusy = busy !== null && busy.kind === 'start' && busy.channelId === channelId
          const stopBusy = busy !== null && busy.kind === 'stop' && busy.channelId === channelId
          const picture = store.meta[channelId]?.imageUrl ?? null
          return (
            <li key={channelId} data-testid='youtube-channel-row'>
              <div className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
                <div className='flex items-center gap-2.5'>
                  <label className='flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-sm'>
                    <input
                      type='checkbox'
                      checked
                      onChange={() => onRemove(channelId)}
                      className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                    />
                    <ProviderBrandIcon src={YOUTUBE_BRAND_ICON_SRC} size='sm' />
                    {picture !== null && (
                      <img
                        src={picture}
                        alt=''
                        referrerPolicy='no-referrer'
                        onError={hideBrokenPicture}
                        data-testid='youtube-channel-picture'
                        className='size-5 shrink-0 rounded object-cover'
                      />
                    )}
                    <span className='truncate text-dm-text-primary' title={channelId}>
                      {titleFor(channelId)}
                    </span>
                  </label>
                  {rowRunning && (
                    <>
                      <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400' aria-hidden='true' />
                      <span className='shrink-0 text-[11px] text-emerald-300'>Live</span>
                      <button
                        type='button'
                        onClick={() => { void handleRowStop(channelId) }}
                        disabled={busy !== null}
                        data-testid='youtube-channel-stop-button'
                        className='inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                      >
                        {stopBusy && <LoadingSpinner />}
                        Stop
                      </button>
                    </>
                  )}
                  {!rowRunning && (
                    <button
                      type='button'
                      onClick={() => openRowPrompt(channelId)}
                      disabled={busy !== null || atLiveCap}
                      data-testid='youtube-channel-start-button'
                      className='inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      Start
                    </button>
                  )}
                </div>
                {row !== null && !rowRunning && row.status === 'STOPPED' && (
                  <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='youtube-channel-ended'>
                    {YOUTUBE_SESSION_ENDED}
                  </p>
                )}
                {rowPrompt !== null && rowPrompt.channelId === channelId && (
                  <div className='mt-2'>
                    <div className='flex gap-1.5'>
                      <input
                        type='text'
                        value={rowPrompt.url}
                        onChange={(e) => setRowPrompt({ channelId, url: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return
                          e.preventDefault()
                          void handleRowStart(channelId)
                        }}
                        placeholder='https://youtube.com/live/...'
                        autoComplete='off'
                        data-testid='youtube-row-url-input'
                        className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-1.5 text-xs text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                      />
                      <button
                        type='button'
                        onClick={() => { void handleRowStart(channelId) }}
                        disabled={busy !== null || atLiveCap || rowPrompt.url.trim() === ''}
                        data-testid='youtube-row-start-button'
                        className='inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                      >
                        {startBusy && <LoadingSpinner />}
                        Start
                      </button>
                      <button
                        type='button'
                        onClick={() => setRowPrompt(null)}
                        disabled={busy !== null}
                        className='inline-flex shrink-0 items-center rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                      >
                        Cancel
                      </button>
                    </div>
                    <p className='mt-1 text-[11px] text-dm-text-accent'>
                      Sessions read one broadcast at a time: paste the
                      channel's CURRENT live video URL (prefilled from
                      the last known broadcast when there was one).
                    </p>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
      <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='youtube-cap-line'>
        {youtubeCapLine(cap, liveCap, channels.length, running)}
      </p>

      {atLiveCap && liveCap !== null && (
        <p className='mt-1 text-[11px] font-medium text-dm-text-secondary' data-testid='youtube-live-cap-note'>
          {youtubeLiveCapNote(liveCap)}
        </p>
      )}

      {upgrade !== null && (
        <p className='mt-1 text-[11px] font-medium text-brand' data-testid='youtube-upgrade-hint'>
          {youtubeUpgradeHint(upgrade, liveCap)}
        </p>
      )}
      {refusal !== null && (
        <div
          data-testid='youtube-channels-refusal'
          className='mt-2 flex items-start gap-2 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-xs text-dm-text-secondary'
        >
          <InformationCircleIcon className='mt-0.5 size-4 shrink-0 text-brand' aria-hidden='true' />
          <span>
            {SESSION_REFUSAL_COPY[refusal.code] ?? refusal.message ?? refusal.fallback}
          </span>
        </div>
      )}
      {error !== null && (
        <div className='mt-2'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
    </div>
  )
}

const ChatDelayInput = ({
  valueMs,
  onCommit,
  className
}: {
  valueMs: number
  onCommit: (ms: number) => void
  className: string
}): JSX.Element => {
  const [text, setText] = useState(formatChatDelaySeconds(valueMs))
  const [badInput, setBadInput] = useState(false)
  const invalid = badInput || parseChatDelaySeconds(text) === 'invalid'
  return (
    <label className='mt-4 block' data-testid='chat-delay-block'>
      <FieldLabel>Message delay (seconds)</FieldLabel>
      <input
        type='number'
        inputMode='decimal'
        min={0}
        max={MAX_CHAT_DELAY_MS / 1000}
        step={0.001}
        value={text}
        aria-label='Message delay in seconds'
        data-testid='chat-delay-input'
        onChange={(e) => {
          const next = e.target.value
          const bad = e.target.validity.badInput
          setText(next)
          setBadInput(bad)
          if (bad) return
          const ms = parseChatDelaySeconds(next)
          if (ms !== 'invalid') onCommit(ms)
        }}
        onBlur={(e) => {
          const formatted = formatChatDelaySeconds(valueMs)
          setText(formatted)
          setBadInput(false)
          if (e.target.value !== formatted) e.target.value = formatted
        }}
        className={className}
      />
      {invalid && (
        <p className='mt-1 text-[11px] text-amber-300'>
          Must be 0 to 60 seconds. The previous delay is kept until this is fixed.
        </p>
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent'>
        Gives your moderators a head start: every message is held this
        long before it appears on the overlay, and messages deleted on
        Twitch during the delay never appear on stream. 0 = instant.
      </p>
    </label>
  )
}

const CUSTOM_WEIGHT = 'CUSTOM'

const ChatWeightField = ({
  label,
  role,
  fontKey,
  valueWeight,
  roleDefault,
  onCommit,
  className
}: {
  label: string
  role: 'username' | 'message'
  fontKey: ChatFontKey
  valueWeight: number
  roleDefault: number
  onCommit: (weight: number) => void
  className: string
}): JSX.Element => {
  const choices = chatWeightChoices(fontKey)
  const effective = effectiveChatFontWeight(fontKey, valueWeight, roleDefault)
  const [customFor, setCustomFor] = useState<ChatFontKey | null>(null)
  const testId = `chat-weight-${role}`

  if (choices.kind === 'fixed') {
    const shipped = choices.presets[0] ?? roleDefault
    return (
      <div className='block'>
        <FieldLabel>{label}</FieldLabel>
        <select
          value={shipped}
          disabled
          aria-label={`${label} (fixed for this font)`}
          data-testid={`${testId}-select`}
          className={`${className} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          <option value={shipped}>{shipped}</option>
        </select>
        <p className='mt-1 text-[11px] text-dm-text-accent' data-testid={`${testId}-hint`}>
          {`This font ships one weight (${shipped}).`}
          {role === 'username' ? ' Usernames show it in the browser\'s simulated bold.' : ''}
        </p>
      </div>
    )
  }

  if (choices.kind === 'cuts') {
    return (
      <div className='block'>
        <FieldLabel>{label}</FieldLabel>
        <select
          value={effective}
          onChange={(e) => onCommit(Number(e.target.value))}
          aria-label={label}
          data-testid={`${testId}-select`}
          className={className}
        >
          {choices.presets.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
        <p className='mt-1 text-[11px] text-dm-text-accent' data-testid={`${testId}-hint`}>
          {`This font ships these weights: ${choices.presets.join(', ')}.`}
        </p>
      </div>
    )
  }

  const axis = choices.custom ?? { min: effective, max: effective }
  const custom = customFor === fontKey || !choices.presets.includes(effective)
  return (
    <div className='block'>
      <FieldLabel>{label}</FieldLabel>
      <select
        value={custom ? CUSTOM_WEIGHT : String(effective)}
        onChange={(e) => {
          const next = e.target.value
          if (next === CUSTOM_WEIGHT) {
            setCustomFor(fontKey)
            return
          }
          setCustomFor(null)
          onCommit(Number(next))
        }}
        aria-label={label}
        data-testid={`${testId}-select`}
        className={className}
      >
        {choices.presets.map((w) => (
          <option key={w} value={String(w)}>{w}</option>
        ))}
        <option value={CUSTOM_WEIGHT}>Custom</option>
      </select>
      {custom && (
        <NumberInput
          value={effective}
          min={axis.min}
          max={axis.max}
          onCommit={onCommit}
          aria-label={`${label} (custom, ${axis.min} to ${axis.max})`}
          data-testid={`${testId}-custom`}
          className={`${className} mt-1.5`}
        />
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent' data-testid={`${testId}-hint`}>
        {fontKey === 'DEFAULT'
          ? `System font: pick a weight, or Custom for any whole number from ${axis.min} to ${axis.max}. Exact rendering depends on the viewer's device.`
          : `Variable font: pick a weight, or Custom for any whole number from ${axis.min} to ${axis.max}.`}
      </p>
    </div>
  )
}

const ChatForm = ({
  existing,
  auth0Token,
  overlayLink,
  onSaved,
  onCancel,
  backLabel,
  multiPlatform
}: {
  existing: ChatOverlaySummary | null
  auth0Token: string | undefined
  overlayLink: AccountOverlayLinkState
  onSaved: (chat: ChatOverlaySummary) => void
  onCancel: () => void
  backLabel: string
  multiPlatform: boolean
}): JSX.Element => {
  const isEdit = existing !== null
  const [draft, setDraft] = useState<ChatDraft>(
    existing === null ? emptyDraft() : draftFromChat(existing)
  )
  const [channelInput, setChannelInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft])
  const [customDimensions, setCustomDimensions] = useState<boolean>(() => {
    if (draft.width === null || draft.height === null) return false
    return chatDimensionPresetKeyOf(draft.width, draft.height) === null
  })

  useEffect(() => {
    void ensureFontLoaded(draft.fontFamily)
  }, [draft.fontFamily])

  const previewConfig = useMemo(() => chatPreviewConfigFromDraft(draft), [draft])
  const previewReducedMotion = usePrefersReducedMotion(true)

  const { status: planStatus, limitFor, upgradeForLimit, account, plans } = useEntitlements()
  const youtubeInvited = account?.youtubeChatInvited === true
  const channelCap = planStatus === 'ready'
    ? limitFor('twitchChannelsPerOverlay')
    : FALLBACK_MAX_CHANNELS
  const atChannelCap = channelCap !== null && draft.twitchChatChannels.length >= channelCap
  const channelUpgrade = atChannelCap
    ? upgradeForLimit('twitchChannelsPerOverlay', channelCap)
    : null

  const { login: myTwitchLogin } = useTwitchConnection()
  const quickAddLogin = atChannelCap
    ? null
    : ownUnlistedLogin(myTwitchLogin, draft.twitchChatChannels)

  const {
    channelId: ownYouTubeChannelId,
    channelTitle: ownYouTubeChannelTitle
  } = useYouTubeConnectionIdentity()

  const youtubeListCap = planStatus === 'ready'
    ? (limitFor('youtubeChannelsPerOverlay') ?? limitFor('twitchChannelsPerOverlay'))
    : FALLBACK_MAX_CHANNELS
  const atYouTubeChannelCap = youtubeListCap !== null &&
    draft.youtubeChatChannels.length >= youtubeListCap
  const youtubeChannelUpgrade = atYouTubeChannelCap && youtubeListCap !== null
    ? plans.find((p) => {
      if (!p.purchasable) return false
      const rungCap = p.limits.youtubeChannelsPerOverlay ?? p.limits.twitchChannelsPerOverlay
      return rungCap === null || rungCap > youtubeListCap
    }) ?? null
    : null
  const youtubeLiveCap = account?.youtubeSessionsPerAccount ?? null

  const addYouTubeChannel = (channelId: string): void => {
    setDraft((d) => {
      if (d.youtubeChatChannels.includes(channelId)) return d
      if (youtubeListCap !== null && d.youtubeChatChannels.length >= youtubeListCap) return d
      return { ...d, youtubeChatChannels: [...d.youtubeChatChannels, channelId] }
    })
  }

  const youtubeSessions = useAccountYouTubeSessions(youtubeInvited && multiPlatform)
  const [youtubeAddBusy, setYoutubeAddBusy] = useState(false)

  const removeYouTubeChannel = (channelId: string): void => {
    setDraft((d) => ({
      ...d,
      youtubeChatChannels: d.youtubeChatChannels.filter((c) => c !== channelId)
    }))
  }

  const removeChannel = (login: string): void => {
    setDraft((d) => ({
      ...d,
      twitchChatChannels: d.twitchChatChannels.filter((c) => c !== login)
    }))
  }

  const addChannels = (): void => {
    const fresh = channelInput
      .split(/[\s,]+/)
      .map(normalizeLogin)
      .filter((c) => c !== '' && /^[a-z0-9_]{1,25}$/.test(c))
    if (fresh.length === 0) return
    setDraft((d) => {
      const merged = [...new Set([...d.twitchChatChannels, ...fresh])]
      return {
        ...d,
        twitchChatChannels: channelCap === null ? merged : merged.slice(0, channelCap)
      }
    })
    setChannelInput('')
  }

  const commitInt = (field: NumericChatField) => (n: number): void => {
    setDraft((d) => ({ ...d, [field]: n }))
  }

  const commitDimension = (field: 'width' | 'height') => (n: number): void => {
    setDraft((d) => ({ ...d, [field]: n }))
  }

  const dimensionSelectValue = ((): string => {
    if (draft.width === null || draft.height === null) return 'NONE'
    if (customDimensions) return 'CUSTOM'
    return chatDimensionPresetKeyOf(draft.width, draft.height) ?? 'CUSTOM'
  })()

  const onDimensionPreset = (key: string): void => {
    if (key === 'NONE') {
      setCustomDimensions(false)
      setDraft((d) => ({ ...d, width: null, height: null }))
      return
    }
    if (key === 'CUSTOM') {
      setCustomDimensions(true)
      setDraft((d) => {
        if (d.width !== null && d.height !== null) return d
        return { ...d, width: 1920, height: 1080 }
      })
      return
    }
    const preset = CHAT_DIMENSION_PRESETS.find((p) => p.key === key)
    if (preset === undefined) return
    setCustomDimensions(false)
    setDraft((d) => ({ ...d, width: preset.width, height: preset.height }))
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting || youtubeAddBusy) return
    setSubmitting(true)
    setError(null)
    try {
      const { blockedWordsText, width, height, youtubeSourceEnabled, ...rest } = draft
      let dimensionVariables: { width?: number, height?: number } = {}
      if (width !== null && height !== null) {
        dimensionVariables = { width, height }
      } else if (existing !== null) {
        dimensionVariables = { width: 0, height: 0 }
      }
      const variables = {
        ...rest,
        ...dimensionVariables,
        name: draft.name.trim(),
        blockedWords: parseBlockedWords(blockedWordsText)
      }
      const { chat } = existing === null
        ? await graphqlRequest<{ chat: ChatOverlaySummary }>(
          createChatOverlayMutation, variables, auth0Token
        )
        : await graphqlRequest<{ chat: ChatOverlaySummary }>(
          updateChatOverlayMutation, { chatId: existing.chatId, ...variables }, auth0Token
        )
      setSubmitting(false)
      setSavedNote(true)
      onSaved(chat)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save chat overlay')
      setSubmitting(false)
    }
  }

  const draftValid = draft.name.trim() !== '' && (
    draft.twitchChatChannels.length > 0 ||
    draft.youtubeChatChannels.length > 0 ||
    draft.youtubeSourceEnabled
  )
  const numberInputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'

  const formTitle = isEdit ? 'Edit chat overlay' : 'New chat overlay'

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset className='contents space-y-1.5'>
          <SectionCard
            title={formTitle}
            action={
              <button
                type='button'
                onClick={onCancel}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
              >
                <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                {backLabel}
              </button>
            }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Main chat, Co-stream chat, …'
                required
                maxLength={80}
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                An identifier, not displayed on the overlay.
              </p>
            </label>
          </SectionCard>

          <SectionCard title='Sources'>
            <div className='mb-4'>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>

              {quickAddLogin !== null && (
                <button
                  type='button'
                  onClick={() => {
                    setDraft((d) => ({
                      ...d,
                      twitchChatChannels: [...new Set([...d.twitchChatChannels, quickAddLogin])]
                    }))
                  }}
                  data-testid='own-channel-quick-add'
                  className='mb-2 inline-flex items-center gap-1.5 rounded-md border border-brand/20 bg-brand/5 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:bg-brand/10'
                >
                  <PlusIcon className='size-3.5 shrink-0 text-brand' aria-hidden='true' />
                  Add your channel:{' '}
                  <span className='font-semibold text-dm-text-primary'>{quickAddLogin}</span>
                </button>
              )}
              <div className='flex gap-1.5'>
                <input
                  type='text'
                  value={channelInput}
                  onChange={(e) => setChannelInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    addChannels()
                  }}
                  disabled={atChannelCap}
                  placeholder='any_channel, another_channel'
                  autoComplete='off'
                  className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'
                />
                <button
                  type='button'
                  onClick={addChannels}
                  disabled={atChannelCap}
                  className='shrink-0 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                >
                  Add
                </button>
              </div>
              <ul className='mt-2 space-y-1.5'>
                {draft.twitchChatChannels.map((login) => (
                  <li key={login}>
                    <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
                      <input
                        type='checkbox'
                        checked
                        onChange={() => removeChannel(login)}
                        className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                      />
                      <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                      <span className='text-dm-text-primary'>{login}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                {channelCap === null
                  ? <>Unlimited channels per overlay ({draft.twitchChatChannels.length} added) — comma or space separated, then Add.</>
                  : <>Up to {channelCap} channels per overlay ({draft.twitchChatChannels.length}/{channelCap}) — comma or space separated, then Add.</>}
              </p>
              {channelUpgrade !== null && (
                <p className='mt-1 text-[11px] font-medium text-brand'>
                  {planDisplayName(channelUpgrade)} allows{' '}
                  {channelUpgrade.limits.twitchChannelsPerOverlay ?? 'unlimited'} channels per overlay.
                </p>
              )}
            </div>

            {youtubeInvited && (
              <>
                <YouTubeChannelsBlock
                  connected={multiPlatform}
                  channels={draft.youtubeChatChannels}
                  savedChannels={existing === null ? null : youtubeChatChannelsOf(existing)}
                  chatId={existing === null ? null : existing.chatId}
                  onDraftAdd={addYouTubeChannel}
                  onRemove={removeYouTubeChannel}
                  onSaved={onSaved}
                  cap={youtubeListCap}
                  liveCap={youtubeLiveCap}
                  upgrade={youtubeChannelUpgrade}
                  ownChannelId={ownYouTubeChannelId}
                  ownChannelTitle={ownYouTubeChannelTitle}
                  auth0Token={auth0Token}
                  store={youtubeSessions}
                  formBusy={submitting}
                  onAddBusyChange={setYoutubeAddBusy}
                />

                {multiPlatform && (
                  <div className='mt-4'>
                    <YouTubeSessionWidget scope={{ kind: 'account' }} variant='card' sessionsStore={youtubeSessions} />
                  </div>
                )}
                <YouTubeTermsFooter />
              </>
            )}
          </SectionCard>

          <SectionCard title='Style'>
            <div className='mb-4 space-y-2'>
              {[
                { field: 'showBadges' as const, label: 'Badges (moderator, subscriber, VIP, …)' },
                { field: 'showChannelPills' as const, label: 'Channel pills (which channel each message came from, when the overlay has more than one channel)' },
                ...(youtubeInvited
                  ? [{ field: 'showYouTubeProfilePictures' as const, label: 'YouTube profile pictures' }]
                  : [])
              ].map(({ field, label }) => (
                <label key={field} className='flex items-center gap-2 text-sm text-dm-text-primary'>
                  <input
                    type='checkbox'
                    checked={draft[field]}
                    onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.checked }))}
                    data-testid={`chat-style-${field}`}
                    className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                  />
                  {label}
                </label>
              ))}
            </div>

            <div className='mb-4' data-testid='chat-emote-providers'>
              <FieldLabel>Emote providers</FieldLabel>
              <div className='space-y-2'>
                {[
                  { field: 'show7tvEmotes' as const, label: '7TV emotes (global + each channel\'s set)' },
                  { field: 'showBttvEmotes' as const, label: 'BTTV emotes (global + each channel\'s set)' },
                  { field: 'showFfzEmotes' as const, label: 'FFZ emotes (global + each channel\'s set)' }
                ].map(({ field, label }) => (
                  <label key={field} className='flex items-center gap-2 text-sm text-dm-text-primary'>
                    <input
                      type='checkbox'
                      checked={draft[field]}
                      onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.checked }))}
                      data-testid={`chat-emote-provider-${field}`}
                      className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                    />
                    {label}
                  </label>
                ))}
              </div>
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Turned-off providers are never loaded on this overlay.
                Changing these makes open overlays check each channel again.
              </p>
            </div>

            <label className='mb-4 block'>
              <FieldLabel>Text shadow</FieldLabel>
              <select
                value={draft.textShadow}
                onChange={(e) => setDraft((d) => ({ ...d, textShadow: e.target.value as TextShadowStyle }))}
                className={numberInputClass}
              >
                <option value='OFF'>Off</option>
                <option value='SOFT'>Soft glow</option>
                <option value='HARD'>Hard outline</option>
              </select>
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Keeps chat readable over bright scenes. Soft is the familiar
                glow; Hard is a crisp 1px outline that stays sharp on
                supersampled browser sources.
              </p>
            </label>

            <label className='mb-4 block'>
              <FieldLabel>Font</FieldLabel>
              <select
                value={draft.fontFamily}
                onChange={(e) => {
                  const fontFamily = e.target.value as ChatFontKey
                  setDraft((d) => ({
                    ...d,
                    fontFamily,
                    messageFontWeight: effectiveChatFontWeight(fontFamily, d.messageFontWeight, CHAT_MESSAGE_WEIGHT_DEFAULT),
                    usernameFontWeight: effectiveChatFontWeight(fontFamily, d.usernameFontWeight, CHAT_USERNAME_WEIGHT_DEFAULT)
                  }))
                }}
                data-testid='chat-font-select'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              >
                <option value={DEFAULT_CHAT_FONT_OPTION.key}>{DEFAULT_CHAT_FONT_OPTION.label}</option>
                {CHAT_FONT_OPTION_GROUPS.map((group) => (
                  <optgroup key={group.category} label={group.label}>
                    {group.options.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>

            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <label className='block'>
                <FieldLabel>Username font size</FieldLabel>
                <NumberInput
                  value={draft.usernameFontSize}
                  min={MIN_FONT_SIZE}
                  max={MAX_FONT_SIZE}
                  onCommit={commitInt('usernameFontSize')}
                  className={numberInputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Username line height</FieldLabel>
                <NumberInput
                  value={draft.usernameLineHeight}
                  min={MIN_LINE_HEIGHT}
                  max={MAX_LINE_HEIGHT}
                  onCommit={commitInt('usernameLineHeight')}
                  className={numberInputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Message font size</FieldLabel>
                <NumberInput
                  value={draft.messageFontSize}
                  min={MIN_FONT_SIZE}
                  max={MAX_FONT_SIZE}
                  onCommit={commitInt('messageFontSize')}
                  className={numberInputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Message line height</FieldLabel>
                <NumberInput
                  value={draft.messageLineHeight}
                  min={MIN_LINE_HEIGHT}
                  max={MAX_LINE_HEIGHT}
                  onCommit={commitInt('messageLineHeight')}
                  className={numberInputClass}
                />
              </label>
              <label className='block'>
                <FieldLabel>Badge &amp; emote scale (%)</FieldLabel>
                <NumberInput
                  value={draft.imageScale}
                  min={MIN_IMAGE_SCALE}
                  max={MAX_IMAGE_SCALE}
                  onCommit={commitInt('imageScale')}
                  className={numberInputClass}
                />
              </label>
              <ColorInput
                label='Message text color'
                value={draft.messageColor}
                onChange={(v) => setDraft((d) => ({ ...d, messageColor: v }))}
              />
              <ChatWeightField
                label='Username weight'
                role='username'
                fontKey={draft.fontFamily}
                valueWeight={draft.usernameFontWeight}
                roleDefault={CHAT_USERNAME_WEIGHT_DEFAULT}
                onCommit={commitInt('usernameFontWeight')}
                className={numberInputClass}
              />
              <ChatWeightField
                label='Message weight'
                role='message'
                fontKey={draft.fontFamily}
                valueWeight={draft.messageFontWeight}
                roleDefault={CHAT_MESSAGE_WEIGHT_DEFAULT}
                onCommit={commitInt('messageFontWeight')}
                className={numberInputClass}
              />
            </div>
            <label className='mt-4 block'>
              <FieldLabel>Blocked words</FieldLabel>
              <textarea
                value={draft.blockedWordsText}
                onChange={(e) => setDraft((d) => ({ ...d, blockedWordsText: e.target.value }))}
                rows={3}
                placeholder='one word or phrase per line'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                One per line, max 100. A message containing any of these
                (case-insensitive, anywhere in the text) never appears on the
                overlay.
              </p>
            </label>
            <ChatDelayInput
              valueMs={draft.chatDelayMs}
              onCommit={(ms) => setDraft((d) => ({ ...d, chatDelayMs: ms }))}
              className={numberInputClass}
            />
          </SectionCard>

          <SectionCard title='Dimensions'>
            <label className='block'>
              <FieldLabel>Overlay size</FieldLabel>
              <select
                value={dimensionSelectValue}
                onChange={(e) => onDimensionPreset(e.target.value)}
                data-testid='chat-dimensions-preset'
                className={numberInputClass}
              >
                <option value='NONE'>None (fill the source)</option>
                {CHAT_DIMENSION_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
                <option value='CUSTOM'>Custom</option>
              </select>
            </label>
            {draft.width !== null && draft.height !== null && (
              <div className='mt-3 grid grid-cols-2 gap-3'>
                <label className='block'>
                  <FieldLabel>Width (px)</FieldLabel>
                  <NumberInput
                    value={draft.width}
                    min={MIN_CHAT_DIMENSION}
                    max={MAX_CHAT_DIMENSION}
                    onCommit={commitDimension('width')}
                    disabled={dimensionSelectValue !== 'CUSTOM'}
                    aria-label='Overlay width in pixels'
                    data-testid='chat-dimension-width'
                    className={`${numberInputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                  />
                </label>
                <label className='block'>
                  <FieldLabel>Height (px)</FieldLabel>
                  <NumberInput
                    value={draft.height}
                    min={MIN_CHAT_DIMENSION}
                    max={MAX_CHAT_DIMENSION}
                    onCommit={commitDimension('height')}
                    disabled={dimensionSelectValue !== 'CUSTOM'}
                    aria-label='Overlay height in pixels'
                    data-testid='chat-dimension-height'
                    className={`${numberInputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                  />
                </label>
              </div>
            )}

            {draft.width !== null && draft.height !== null && (
              <p
                className='mt-2 text-[11px] font-medium text-dm-text-secondary'
                data-testid='chat-dimensions-obs-hint'
              >
                {obsSourceRecipeFor(draft.width, draft.height)}
              </p>
            )}
          </SectionCard>
        </fieldset>
      </div>

      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          <div className='flex items-center gap-2'>
            <button
              type='submit'
              disabled={submitting || youtubeAddBusy || !draftValid}
              className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {submitting && <LoadingSpinner />}
              {isEdit ? 'Save changes' : 'Create chat overlay'}
            </button>
            <button
              type='button'
              onClick={onCancel}
              disabled={submitting}
              className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
            >
              Cancel
            </button>
          </div>
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='chat-overlay-saved' />}

          {isEdit && (
            <>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                Browser source URL
              </h4>
              <OverlayLinkBlock link={overlayLink} />
            </>
          )}
          {!isEdit && (
            <p className='text-[11px] text-dm-text-accent'>
              The browser-source link appears here (and in the list) after the
              first save.
            </p>
          )}

          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>

          {draft.width !== null && draft.height !== null
            ? (
              <DimensionedPreviewBox width={draft.width} height={draft.height}>
                <ChatOverlayMockup config={previewConfig} fillHost />
              </DimensionedPreviewBox>
              )
            : (
              <div
                data-testid='chat-preview-stage'
                className='overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600'
                style={{ height: MOCKUP_STAGE_HEIGHT_PX }}
              >
                <ChatOverlayMockup config={previewConfig} fillHost />
              </div>
              )}
          <p className='text-[11px] text-dm-text-accent'>
            Sample chat: made-up names and lines, styled exactly like your
            overlay. The live overlay shows real chat from the selected
            channels.
          </p>
          {previewReducedMotion && (
            <p className='text-[11px] text-dm-text-accent' data-testid='chat-preview-reduced-motion'>
              Your system asks for reduced motion, so the sample chat holds
              still here. The live overlay still scrolls as messages arrive.
            </p>
          )}
        </div>
      </div>
    </form>
  )
}

const ChatListRow = ({
  chat,
  onEdit,
  onDuplicate,
  onDelete,
  busyAction,
  atCap
}: {
  chat: ChatOverlaySummary
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  busyAction: null | 'delete' | 'duplicate'
  atCap: boolean
}): JSX.Element => {
  const busy = busyAction != null
  const overlayLink = useAccountOverlayLink('chat', chat.chatId)
  const sources = [...chat.twitchChatChannels, ...youtubeChatChannelsOf(chat)]
  return (
    <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
            Chat
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>
              {chat.name}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {sources.length} channel{sources.length === 1 ? '' : 's'} · {sources.join(', ')}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <OverlayLinkRowActions link={overlayLink} label={`${chat.name} chat overlay`} />
          <button
            type='button'
            onClick={onEdit}
            title='Edit'
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PencilSquareIcon className='size-3.5' aria-hidden='true' />
            Edit
          </button>
          <button
            type='button'
            onClick={onDuplicate}
            title={atCap ? "Your account's chat-overlay limit is reached" : 'Duplicate'}
            disabled={busy || atCap}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
            Duplicate
          </button>
          <button
            type='button'
            onClick={onDelete}
            disabled={busy}
            title='Delete'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

export type QuickOverlayManagerView = 'list' | 'create' | { edit: string }

export const ChatOverlayManager = ({
  view,
  basePath,
  backLabel
}: {
  view: QuickOverlayManagerView
  basePath: string
  backLabel: string
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const navigate = useNavigate()
  const { youtubeConnected } = useYouTubeConnection()

  const [chats, setChats] = useState<ChatOverlaySummary[] | null>(null)
  const [listBusy, setListBusy] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const loadChats = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setListBusy(true)
    setListError(null)
    try {
      const { chats: fetched } = await graphqlRequest<{ chats: ChatOverlaySummary[] }>(
        listMyChatOverlaysQuery, {}, auth0Token
      )
      setChats(fetched)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load chat overlays')
    } finally {
      setListBusy(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void loadChats()
  }, [loadChats])

  const { enforcedLimitFor, upgradeForLimit } = useEntitlements()
  const overlayCap = enforcedLimitFor('chatOverlaysPerAccount')
  const chatCount = chats === null ? 0 : chats.length
  const atOverlayCap = overlayCap !== null && chatCount >= overlayCap
  const overlayUpgrade = atOverlayCap
    ? upgradeForLimit('chatOverlaysPerAccount', overlayCap)
    : null

  const [rowBusy, setRowBusy] = useState<{ id: string, action: 'delete' | 'duplicate' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const goList = (): void => { void navigate(basePath) }
  const goCreate = (): void => { void navigate(`${basePath}/new`) }
  const goEdit = (chatId: string): void => { void navigate(`${basePath}/${chatId}`) }

  const editingId = typeof view === 'object' ? view.edit : null
  const editing = useMemo<ChatOverlaySummary | null>(() => {
    if (editingId === null || chats === null) return null
    return chats.find((c) => c.chatId === editingId) ?? null
  }, [editingId, chats])
  const editLink = useAccountOverlayLink('chat', editing === null ? null : editing.chatId)

  const upsertChat = (chat: ChatOverlaySummary): void => {
    setChats((prev) => {
      if (prev === null) return [chat]
      const exists = prev.some((c) => c.chatId === chat.chatId)
      return exists
        ? prev.map((c) => c.chatId === chat.chatId ? chat : c)
        : [...prev, chat]
    })
  }

  const removeChat = (chatId: string): void => {
    setChats((prev) => prev === null ? prev : prev.filter((c) => c.chatId !== chatId))
  }

  const handleDelete = async (chat: ChatOverlaySummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    const ok = globalThis.confirm(
      `Delete chat overlay "${chat.name}"? Any browser source still pointing at its URL goes permanently blank.`
    )
    if (!ok) return
    setRowBusy({ id: chat.chatId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteChatOverlayMutation, { chatId: chat.chatId }, auth0Token
      )
      if (result.deleted) removeChat(chat.chatId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete chat overlay')
    } finally {
      setRowBusy(null)
    }
  }

  const handleDuplicate = async (chat: ChatOverlaySummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    setRowBusy({ id: chat.chatId, action: 'duplicate' })
    setRowError(null)
    try {
      const { chat: created } = await graphqlRequest<{ chat: ChatOverlaySummary | null }>(
        duplicateChatOverlayMutation, { chatId: chat.chatId }, auth0Token
      )
      if (created == null) {
        setRowError('Failed to duplicate chat overlay')
        return
      }
      upsertChat(created)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to duplicate chat overlay')
    } finally {
      setRowBusy(null)
    }
  }

  if (view === 'create') {
    return (
      <ChatForm
        existing={null}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={(chat) => {
          upsertChat(chat)
          void navigate(`${basePath}/${chat.chatId}`, { replace: true })
        }}
        onCancel={goList}
        backLabel={backLabel}
        multiPlatform={youtubeConnected}
      />
    )
  }

  if (typeof view === 'object') {
    if (chats === null) {
      return (
        <div className='mt-4 flex items-center justify-center px-4 pb-4 py-10'>
          <LoadingSpinner />
        </div>
      )
    }
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That chat overlay no longer exists.</ErrorInline>
          <button onClick={goList} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            {backLabel}
          </button>
        </div>
      )
    }
    return (
      <ChatForm
        existing={editing}
        auth0Token={auth0Token}
        overlayLink={editLink}
        onSaved={upsertChat}
        onCancel={goList}
        backLabel={backLabel}
        multiPlatform={youtubeConnected}
      />
    )
  }

  let listContent: JSX.Element
  if (chats === null) {
    listContent = (
      <div className='flex items-center justify-center rounded-lg border border-dashed border-white/10 px-4 py-10'>
        <LoadingSpinner />
      </div>
    )
  } else if (chats.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <ChatBubbleLeftRightIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>No chat overlays yet.</p>
        <button
          type='button'
          onClick={goCreate}
          className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
        >
          <PlusIcon className='size-3.5' aria-hidden='true' />
          Create the first one
        </button>
      </div>
    )
  } else {
    listContent = (
      <ul className='space-y-2' data-testid='chat-overlays-list'>
        {sortByName(chats, (c) => c.name, (c) => c.chatId).map((chat) => (
          <ChatListRow
            key={chat.chatId}
            chat={chat}
            onEdit={() => goEdit(chat.chatId)}
            onDuplicate={() => { void handleDuplicate(chat) }}
            onDelete={() => { void handleDelete(chat) }}
            busyAction={rowBusy?.id === chat.chatId ? rowBusy.action : null}
            atCap={atOverlayCap}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Chat Overlays'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void loadChats() }}
              disabled={listBusy}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {listBusy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
              Refresh
            </button>
            <button
              type='button'
              data-testid='add-chat-overlay-button'
              onClick={goCreate}
              disabled={atOverlayCap}
              title={atOverlayCap ? "Your account's chat-overlay limit is reached" : undefined}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              Add new chat overlay
            </button>
          </div>
        }
      >
        {listError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setListError(null)}>{listError}</ErrorInline>
          </div>
        )}
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {atOverlayCap && (
          <div className='mb-3 rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] text-dm-text-secondary'>
            Your account has reached its chat-overlay limit ({overlayCap}).
            {overlayUpgrade !== null && (
              <> <span className='font-medium text-brand'>{planDisplayName(overlayUpgrade)} allows {overlayUpgrade.limits.chatOverlaysPerAccount ?? 'unlimited'} per account.</span></>
            )}
          </div>
        )}

        {listContent}

        <div className='mt-4 space-y-1 text-[11px] text-dm-text-accent'>
          <p>
            Each chat overlay has its own browser-source URL. Add it to your streaming software with a transparent background.
          </p>
          <p>{OVERLAY_LINK_NOTE}</p>
        </div>
      </SectionCard>
    </div>
  )
}
